import { useEffect, useMemo, useState } from "react";
import AppShell from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";
import { api, inr, formatErr } from "@/lib/api";
import AllergenBadge from "@/components/AllergenBadge";
import { toast } from "sonner";
import { ShoppingBag, Plus, Minus, Trash2, ChefHat, Zap } from "lucide-react";

export default function InstaShop() {
  const { user, refresh } = useAuth();
  const [menu, setMenu] = useState([]);
  const [cats, setCats] = useState([]);
  const [activeCat, setActiveCat] = useState("All");
  const [cart, setCart] = useState({});
  const [children, setChildren] = useState([]);
  const [studentId, setStudentId] = useState(null);
  const [placing, setPlacing] = useState(false);

  useEffect(() => {
    // Load live daily menu
    api.get("/menu?mode=daily&hide_sold_out=true").then(r => setMenu(r.data));
    api.get("/menu/categories").then(r => setCats(["All", ...r.data]));
    if (user?.role === "parent") {
      api.get("/parent/children").then(r => {
        setChildren(r.data);
        if (r.data.length) setStudentId(r.data[0].id);
      });
    } else {
      setStudentId(user?.id);
    }
  }, [user]);

  const filtered = useMemo(() =>
    menu.filter(m => activeCat === "All" || m.category === activeCat),
    [menu, activeCat]
  );

  const cartItems = useMemo(() =>
    Object.entries(cart).map(([id, qty]) => {
      const m = menu.find(x => x.id === id);
      return m ? { ...m, qty } : null;
    }).filter(Boolean),
    [cart, menu]
  );
  const total = cartItems.reduce((s, c) => s + c.price * c.qty, 0);

  const inc = (id) => setCart(c => ({ ...c, [id]: (c[id] || 0) + 1 }));
  const dec = (id) => setCart(c => {
    const next = { ...c };
    next[id] = (c[id] || 0) - 1;
    if (next[id] <= 0) delete next[id];
    return next;
  });

  const placeOrder = async () => {
    if (!studentId) return toast.error("Select a student");
    if (cartItems.length === 0) return toast.error("Cart is empty");
    setPlacing(true);
    try {
      await api.post("/orders", {
        student_id: studentId,
        items: cartItems.map(c => ({ menu_item_id: c.id, qty: c.qty })),
        type: "qr",
        payment_method: "wallet",
      });
      toast.success("Order placed! Wallet debited. Enjoy your meal!");
      setCart({});
      refresh();
    } catch (e) { toast.error(formatErr(e)); }
    setPlacing(false);
  };

  return (
    <AppShell variant={user?.role === "student" ? "student" : user?.role === "staff" ? "staff" : "parent"}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
          <div>
            <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-amber-600">
              <Zap size={14} /> InstaShop
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-ink mt-2">Order now, eat now</h1>
            <p className="text-sm text-ink-500 mt-1">Browse the live canteen menu and pay instantly from your wallet.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            {user?.role === "parent" && children.length > 0 && (
              <select
                data-testid="instashop-child"
                value={studentId || ""}
                onChange={(e) => setStudentId(e.target.value)}
                className="rounded-full border border-border bg-white px-4 py-2.5 text-sm font-semibold focus:border-brand focus:outline-none"
              >
                {children.map(c => <option key={c.id} value={c.id}>{c.name} · {c.grade}</option>)}
              </select>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 mb-6 overflow-x-auto no-scrollbar pb-1">
          {cats.map(c => (
            <button
              key={c}
              onClick={() => setActiveCat(c)}
              data-testid={`instashop-cat-${c}`}
              className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${activeCat === c ? "bg-ink text-white" : "bg-cream-200 text-ink-700 hover:bg-cream-300"}`}
            >
              {c}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {filtered.map(m => (
              <div key={m.id} className="bg-white rounded-3xl border border-border overflow-hidden shadow-soft hover:shadow-medium transition-shadow flex flex-col">
                <div className="aspect-[4/3] bg-cream-200 overflow-hidden relative">
                  {m.image_url ? (
                    <img src={m.image_url} alt={m.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-ink-400"><ChefHat size={32} /></div>
                  )}
                  {m.sold_out && (
                    <div className="absolute inset-0 bg-ink/60 flex items-center justify-center">
                      <span className="text-white font-heading font-bold text-lg">Sold out</span>
                    </div>
                  )}
                </div>
                <div className="p-4 flex flex-col flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-heading font-bold text-ink">{m.name}</div>
                      <div className="text-[11px] uppercase tracking-wider text-ink-500 mt-0.5">{m.category}</div>
                    </div>
                    <div className="font-heading text-lg font-extrabold text-ink">{inr(m.price)}</div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-1">
                    {m.dietary_tags?.map(t => <AllergenBadge key={t} tag={t} />)}
                  </div>
                  {m.nutrition && (
                    <div className="mt-2 text-[11px] text-ink-500">
                      {m.nutrition.calories} kcal · {m.nutrition.protein}g protein · {m.nutrition.carbs}g carbs · {m.nutrition.fat}g fat
                    </div>
                  )}
                  <div className="mt-auto pt-4 flex items-center justify-between">
                    {cart[m.id] ? (
                      <div className="flex items-center gap-2">
                        <button data-testid={`is-dec-${m.id}`} onClick={() => dec(m.id)} className="w-9 h-9 rounded-full bg-cream-200 text-ink hover:bg-cream-300 flex items-center justify-center"><Minus size={16} /></button>
                        <span className="font-heading font-bold w-6 text-center">{cart[m.id]}</span>
                        <button data-testid={`is-inc-${m.id}`} onClick={() => inc(m.id)} className="w-9 h-9 rounded-full bg-ink text-white hover:bg-ink-700 flex items-center justify-center"><Plus size={16} /></button>
                      </div>
                    ) : (
                      <button
                        data-testid={`is-add-${m.id}`}
                        disabled={m.sold_out}
                        onClick={() => inc(m.id)}
                        className="px-4 py-2 rounded-full bg-amber-500 text-white text-sm font-semibold hover:bg-amber-600 active:scale-95 transition-all disabled:opacity-50"
                      >
                        Add
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Cart sidebar */}
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-24 bg-white rounded-3xl border border-border p-6 shadow-soft">
              <div className="flex items-center gap-2 text-ink">
                <ShoppingBag size={18} />
                <div className="font-heading text-xl font-bold">Your order</div>
              </div>
              {cartItems.length === 0 ? (
                <div className="mt-6 text-sm text-ink-500">Pick something from the menu to order instantly.</div>
              ) : (
                <>
                  <div className="mt-4 space-y-3 max-h-72 overflow-y-auto">
                    {cartItems.map(c => (
                      <div key={c.id} className="flex items-center gap-3" data-testid={`is-cart-${c.id}`}>
                        <div className="flex-1">
                          <div className="font-semibold text-ink text-sm">{c.name}</div>
                          <div className="text-xs text-ink-500">{c.qty} x {inr(c.price)}</div>
                        </div>
                        <div className="font-bold text-ink">{inr(c.price * c.qty)}</div>
                        <button onClick={() => setCart(({[c.id]:_, ...rest}) => rest)} className="text-ink-400 hover:text-brand"><Trash2 size={16} /></button>
                      </div>
                    ))}
                  </div>
                  <div className="border-t border-border mt-4 pt-4 flex items-center justify-between">
                    <span className="text-sm text-ink-500">Total</span>
                    <span className="font-heading text-2xl font-extrabold text-ink" data-testid="instashop-total">{inr(total)}</span>
                  </div>
                  <button
                    data-testid="instashop-place-order"
                    onClick={placeOrder}
                    disabled={placing || !studentId}
                    className="mt-4 w-full px-5 py-3 rounded-full bg-amber-500 text-white font-semibold hover:bg-amber-600 active:scale-95 transition-all disabled:opacity-50"
                  >
                    {placing ? "Placing..." : `Pay ${inr(total)} from wallet`}
                  </button>
                  <div className="mt-2 text-[11px] text-ink-500 text-center">Instant order · deducted from wallet</div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
